/**
 * TocynID plan definitions — SINGLE SOURCE OF TRUTH.
 *
 * These definitions must stay in sync with:
 *   1. Stripe price IDs (Randy sets up Stripe Products + Prices separately)
 *   2. Backend plan validation (Randy's migration 0004+ adds tenants.plan)
 *   3. Marketing pricing page at tocynid.com
 *
 * If prices change, update here FIRST and then alert Randy + check
 * the marketing site.
 *
 * "founder" is a special tier — not selectable in the UI. It's
 * applied manually by admin DB update for Dani and early adopters.
 * Bypass billing entirely (stripe_subscription_id=NULL).
 */

export const PLANS = {
  starter: {
    key: 'starter',
    name: 'Starter',
    price_monthly_pence: 599,            // £5.99/mo
    price_annual_pence: 5990,            // £59.90/year — 2 months free
    max_staff: 10,
    features: [
      'Up to 10 active staff',
      'QR verification',
      'Basic dashboard',
      'Email support',
    ],
    description: 'For small teams getting started with staff verification.',
    cta: 'Choose Starter',
  },
  standard: {
    key: 'standard',
    name: 'Standard',
    price_monthly_pence: 1299,           // £12.99/mo
    price_annual_pence: 12990,           // £129.90/year
    max_staff: 50,
    features: [
      'Up to 50 active staff',
      'Custom domain support',
      'Full branding (logo, colours, tagline)',
      'Scan logs with geolocation',
      'Priority email support',
    ],
    description: 'For established teams that need branded verification.',
    cta: 'Choose Standard',
    popular: true,
  },
  professional: {
    key: 'professional',
    name: 'Professional',
    price_monthly_pence: 2599,           // £25.99/mo
    price_annual_pence: 25990,           // £259.90/year
    max_staff: null,                     // null = unlimited
    features: [
      'Unlimited active staff',
      'Multi-site organisation',
      'Role-based admin access',
      'Audit history',
      'API access',
      'Priority support with SLA',
    ],
    description: 'For larger organisations with complex verification needs.',
    cta: 'Choose Professional',
  },
  founder: {
    // NOT user-selectable. Applied manually for lifetime founder rate.
    key: 'founder',
    name: 'Founder',
    price_monthly_pence: 0,
    price_annual_pence: 0,
    max_staff: null,
    features: [
      'All Professional features',
      'Lifetime free for early adopters',
      'Direct line to the founders',
    ],
    description: 'Reserved for TocynID founding customers.',
    cta: null,
    hidden: true,
  },
};

/**
 * Cost Per Badge Activation — usage fee for staff over the plan's max.
 * Applied as a metered usage line on the Stripe subscription.
 */
export const CPBA_PENCE = 200;  // £2.00 per badge activation

/**
 * Plan helpers — utilities used by the UI.
 */

export function getPlan(key) {
  return PLANS[key] || PLANS.starter;
}

export function getSelectablePlans() {
  // Returns only user-selectable plans (excludes founder).
  return [PLANS.starter, PLANS.standard, PLANS.professional];
}

/**
 * Compare two plans for upgrade/downgrade direction.
 * Returns 1 if `to` is higher-tier than `from`, -1 if lower, 0 if same.
 * Ordering: starter < standard < professional. Founder is off-ladder.
 */
export function planDirection(fromKey, toKey) {
  const order = { starter: 1, standard: 2, professional: 3, founder: 999 };
  const a = order[fromKey] || 0;
  const b = order[toKey] || 0;
  if (a === b) return 0;
  return b > a ? 1 : -1;
}

/**
 * Format plan price for display. Defaults to monthly.
 */
export function formatPlanPrice(plan, period = 'monthly') {
  const pence = period === 'annual'
    ? plan.price_annual_pence
    : plan.price_monthly_pence;
  if (pence === 0) return 'Free';
  const pounds = (pence / 100).toFixed(pence % 100 === 0 ? 0 : 2);
  return `£${pounds}/${period === 'annual' ? 'year' : 'mo'}`;
}
